import React from 'react';
import { IonButton, useIonRouter } from '@ionic/react';
import { INTENT_NOUN, type MessageIntent } from '@/modules/template';
import {
  ComposeLogic,
  EnvironmentLogic,
  SlotControl,
  SlotLogic,
  type AnsweredSlot,
  type EvaluatedForm,
  type SlotRecipients,
} from '@/modules/form';
import { formatMutationTimestamp } from '@/modules/topic/mutations';
import { useShareWizardContext } from '@/contexts/ShareWizardContext';
import { INTENT_SYMBOL } from './logic';
import type { AddressingMode } from './types';
import UpdateSummaryField from './UpdateSummaryField';
import ConnectionField from './ConnectionField';
import styles from './ComposerFormTab.module.css';

/** One message's questions, and what they are asked against. */
interface IntentQuestions {
  intent: MessageIntent;
  evaluated: EvaluatedForm;
  addressingMode: AddressingMode;
  recipients: SlotRecipients;
  questions: ReadonlyArray<{ slot: AnsweredSlot; name: string }>;
}

/**
 * The questions one intent's message asks the sender, in body order.
 *
 * The garden form is answered here directly: the slots come from
 * `ComposeLogic.answerableSlots`, already free of the ones this message leaves
 * out, and each is keyed by the field id its answer is stored under — the key
 * the rest of the session reads. A slot soft-linked to one an earlier message
 * asks is left out too: the sender answers the question once, and the answer
 * is stored under that slot's id as well, so its message prints it.
 */
function useIntentQuestions(): (intent: MessageIntent) => IntentQuestions {
  const {
    sharingMode,
    getEnvironmentForIntent,
    getMarkedEnvironmentForRecipient,
    getContactsForIntent,
    addressingModeForIntent,
    isAnsweredByLinkedSlot,
  } = useShareWizardContext();

  return intent => {
    const addressingMode = addressingModeForIntent(intent);
    const intentRecipients = getEnvironmentForIntent(intent);
    const evaluated = ComposeLogic.evaluateForm(intentRecipients);
    const questions = ComposeLogic.answerableSlots(
      EnvironmentLogic.factsOf(intentRecipients).form,
      evaluated,
      { sharingMode, addressingMode }
    )
      .filter(slot => !isAnsweredByLinkedSlot(slot.id))
      .map(slot => ({ slot, name: slot.id }));
    const recipients: SlotRecipients = {
      contacts: getContactsForIntent(intent),
      markedFormFor: recipient =>
        ComposeLogic.evaluateForm(
          getMarkedEnvironmentForRecipient(intent, recipient)
        ),
    };
    return { intent, evaluated, addressingMode, recipients, questions };
  };
}

/**
 * One message's questions, each rendered by the control its own slot type
 * declares. Nothing here knows what kind of slot it is showing.
 */
const IntentBlock: React.FC<{
  section: IntentQuestions;
  /** Whether to say which message these questions are for. */
  showHeading: boolean;
  /** The field id of the question to put the profile-names link under, if any. */
  profileNamesLinkSlotId: string | null;
}> = ({ section, showHeading, profileNamesLinkSlotId }) => {
  const {
    inProgressLegId,
    answerSlot,
    writeIns,
    setWriteIn,
    allowedDetailSlotIdForIntent,
  } = useShareWizardContext();
  const { intent, evaluated, addressingMode, recipients, questions } = section;
  const allowedDetailSlotId = allowedDetailSlotIdForIntent(intent);

  return (
    <section
      className={styles.intent}
      data-testid={`garden-composer-intent-${intent}`}
    >
      {showHeading && (
        <h2 className={styles.intentHeading}>
          {INTENT_SYMBOL[intent]} {INTENT_NOUN[intent].one}
        </h2>
      )}
      {questions.map(({ slot, name }) => (
        <React.Fragment key={name}>
          <SlotControl
            slot={slot}
            env={
              evaluated.slotEnvironments.get(slot.id) ??
              evaluated.resultingEnvironment
            }
            addressingMode={addressingMode}
            recipients={recipients}
            disabled={inProgressLegId != null}
            isAllowedDetail={name === allowedDetailSlotId}
            testId={`garden-composer-${intent}-${name}`}
            onAnswer={answer => answerSlot(name, answer)}
            lastWriteIn={writeIns[name]}
            onLeaveWriteIn={words => setWriteIn(name, words)}
          />
          {name === profileNamesLinkSlotId && <EditProfileNamesLink />}
        </React.Fragment>
      ))}
    </section>
  );
};

/**
 * A link to the profile's Attribution step, where the names a slot offers the
 * sender are edited. Its return button comes back here, and the slot's options
 * read the edited names, since a picked name is stored as the primitive that
 * reads it rather than as the name itself.
 */
const EditProfileNamesLink: React.FC = () => {
  const router = useIonRouter();
  const editProfileNames = () =>
    router.push(
      `/profile/steps/attribution?returnButtonLabel=${encodeURIComponent(
        'Back to sharing'
      )}`,
      'forward',
      'push'
    );
  return (
    <div className={styles.editNamesRow}>
      <IonButton
        fill="clear"
        size="small"
        className={styles.editNames}
        data-testid="garden-composer-edit-profile-names"
        onClick={editProfileNames}
      >
        Edit profile names
      </IonButton>
    </div>
  );
};

/**
 * What the sender says in an update: the change it reports on, and their own
 * summary of it, which the update's body carries.
 */
const ChangeSummary: React.FC = () => {
  const { aboutChangesSince, updateSummary, setUpdateSummary } =
    useShareWizardContext();
  return (
    <section
      className={styles.changeSummary}
      data-testid="garden-composer-change-summary"
    >
      {aboutChangesSince && (
        <h2 className={styles.since} data-testid="garden-composer-change-since">
          Since {formatMutationTimestamp(aboutChangesSince)}
        </h2>
      )}
      <UpdateSummaryField
        value={updateSummary}
        onChange={setUpdateSummary}
        testId="garden-composer-update-summary"
      />
    </section>
  );
};

/**
 * Where the connection question's write-in is remembered. Not a form field, so
 * it cannot collide with one: field ids are UUID80s.
 */
const CONNECTION_WRITE_IN_KEY = 'connectionToPrevLeg';

/**
 * How the sender knows whoever passed this on to them, asked once when any
 * message this reshare sends tells the story of how it reached them. A first
 * share, or a reshare whose messages carry no story, asks nothing.
 */
const ResharerConnection: React.FC = () => {
  const {
    sharingMode,
    intentsWithRecipients,
    getFormForIntent,
    connectionToPrevLeg,
    setConnectionToPrevLeg,
    inProgressLegId,
    writeIns,
    setWriteIn,
  } = useShareWizardContext();
  const tellsStory =
    sharingMode === 'reshare' &&
    intentsWithRecipients.some(intent => {
      const form = getFormForIntent(intent);
      return form !== null && ComposeLogic.contributesReshareStory(form);
    });
  if (!tellsStory) return null;
  return (
    <ConnectionField
      value={connectionToPrevLeg}
      onChange={setConnectionToPrevLeg}
      disabled={inProgressLegId != null}
      lastWriteIn={writeIns[CONNECTION_WRITE_IN_KEY]}
      onLeaveWriteIn={words => setWriteIn(CONNECTION_WRITE_IN_KEY, words)}
    />
  );
};

/**
 * The composer's Form tab (mock 9c): everything the sender fills in, for every
 * message this share sends, on one surface.
 *
 * A reshare's connection to whoever sent it comes first when its messages tell
 * the story: it is one answer for every message. Then each intent with
 * recipients contributes its questions in compose-step order, under a heading
 * once there is more than one message to tell apart. The first question that
 * offers the sender's profile name links to where that name is edited. In a fresh update the
 * change summary comes before them, since it is what the update says. A
 * reshared update passes on the summary it received unchanged, so there is
 * nothing to write; it shows in the message preview. A message with
 * nothing to answer adds nothing, so a form made only of text — or a topic
 * that composes the classic way, which has no form to answer — leaves the tab
 * empty rather than failing.
 */
const ComposerFormTab: React.FC = () => {
  const { isUpdateFlow, sharingMode, intentsWithRecipients } =
    useShareWizardContext();
  const questionsFor = useIntentQuestions();

  const sections = intentsWithRecipients
    .map(questionsFor)
    .filter(section => section.questions.length > 0);
  // Only the first question offering the sender's profile name carries the
  // link to edit it, however many messages ask for it.
  const profileNamesLink = sections
    .flatMap(section =>
      section.questions.map(({ slot, name }) => ({
        intent: section.intent,
        name,
        slot,
      }))
    )
    .find(({ slot }) => SlotLogic.namesSender(slot));

  return (
    <div data-testid="garden-composer-form">
      <ResharerConnection />
      {isUpdateFlow && sharingMode === 'fresh' && <ChangeSummary />}
      {intentsWithRecipients.length === 0 ? (
        <p className={styles.empty} data-testid="garden-composer-no-recipients">
          Choose who this goes to, and the message to fill in appears here.
        </p>
      ) : (
        sections.map(section => (
          <IntentBlock
            key={section.intent}
            section={section}
            showHeading={sections.length > 1}
            profileNamesLinkSlotId={
              profileNamesLink?.intent === section.intent
                ? profileNamesLink.name
                : null
            }
          />
        ))
      )}
    </div>
  );
};

export default ComposerFormTab;
